import React, { useEffect, useRef } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import { useModalStyles } from "../../common/useModalStyles";

export interface RecipientOption {
  value: number;
  label: string;
}

interface CcBccFieldProps {
  label: string;
  value: RecipientOption[];
  onChange: (val: RecipientOption[]) => void;
  options: RecipientOption[];
  excludeIds?: number[];
  onHide?: () => void;
  endAdornment?: React.ReactNode;
}

const CcBccField: React.FC<CcBccFieldProps> = ({
  label,
  value,
  onChange,
  options,
  excludeIds = [],
  onHide,
  endAdornment,
}) => {
  const { tokens: mTokens, isDark } = useModalStyles();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  const handleBlur = () => {
    setTimeout(() => {
      if (
        value.length === 0 &&
        wrapperRef.current &&
        !wrapperRef.current.contains(document.activeElement)
      ) {
        onHide?.();
      }
    }, 150);
  };

  const filtered = options.filter(
    (o) =>
      !excludeIds.includes(o.value) &&
      !value.find((v) => v.value === o.value)
  );

  return (
    <Box
      ref={wrapperRef}
      display="flex"
      alignItems="center"
      gap={1}
      px={2}
      py={0.75}
      onBlur={handleBlur}
      sx={{
        borderBottom: `1px solid ${mTokens.border}`,
        animation: "ccFadeIn 0.15s ease",
        "@keyframes ccFadeIn": {
          from: { opacity: 0, transform: "translateY(-4px)" },
          to:   { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          minWidth: 30,
          fontWeight: "bold",
          userSelect: "none",
          fontSize: "0.82rem",
          flexShrink: 0,
          color: mTokens.textSecondary,
        }}
      >
        {label}:
      </Typography>

      <Box flex={1} minWidth={0}>
        <Autocomplete
          multiple
          fullWidth
          size="small"
          options={filtered}
          getOptionLabel={(opt) => opt.label}
          value={value}
          onChange={(_, newVal) => onChange(newVal)}
          filterSelectedOptions
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                size="small"
                sx={{
                  backgroundColor: isDark
                    ? "rgba(20,184,166,0.16)"
                    : "rgba(13,148,136,0.12)",
                  color: mTokens.accent,
                  border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(13,148,136,0.25)"}`,
                  fontSize: "0.73rem",
                  height: 22,
                  fontWeight: 600,
                  "& .MuiChip-deleteIcon": {
                    color: isDark ? "rgba(20,184,166,0.7)" : "rgba(13,148,136,0.7)",
                  },
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder={value.length === 0 ? `Add ${label} recipients…` : ""}
              inputRef={inputRef}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                sx: { fontSize: "0.875rem", color: mTokens.textPrimary },
              }}
            />
          )}
          sx={{
            "& .MuiAutocomplete-tag": { my: 0.25 },
            "& .MuiInput-root": { py: 0.25 },
          }}
        />
      </Box>

      {endAdornment && (
        <Box display="flex" alignItems="center" flexShrink={0} gap={0.5} ml={1}>
          {endAdornment}
        </Box>
      )}
    </Box>
  );
};

export default CcBccField;